/**
 * Vault-equivalent values for every Atlas semantic typography role.
 *
 * Typed as `AtlasTypographySemantic` so TypeScript enforces shape parity.
 * Strategy: keep the same px sizes as Atlas (to avoid layout shifts),
 * swap font-family from Plus Jakarta Sans → Rubik.
 *
 * Mapping rationale documented in `.cursor/atlas-ff-token-mapping.md`.
 * Source: Vault `newTypography` from `main` branch.
 */

import type {
  AtlasTypographySemantic,
  AtlasTypographyToken,
} from '../typography';

/** Vault heading font — matches `newTypography` Rubik usage on main branch */
const vaultHeadingFontFamily = '"Rubik", sans-serif';
/** Vault body font — matches `theme.typography.fontFamily` Ubuntu usage on main branch */
const vaultBodyFontFamily = '"Ubuntu", sans-serif';

export const vaultTypographyOverrides = {
  headingH1: {
    fontFamily: vaultHeadingFontFamily,
    fontWeight: 600,
    fontSize: 30, // same px as Atlas headingH1 to prevent layout shift
    lineHeight: 38, // same as Atlas
    letterSpacing: 0,
  },
  headingH2: {
    fontFamily: vaultHeadingFontFamily,
    fontWeight: 600,
    fontSize: 26, // same px as Atlas headingH2
    lineHeight: 34,
    letterSpacing: 0,
  },
  headingH3: {
    fontFamily: vaultHeadingFontFamily,
    fontWeight: 600,
    fontSize: 22, // same px as Atlas headingH3
    lineHeight: 28,
    letterSpacing: 0,
  },
  headingH4: {
    fontFamily: vaultHeadingFontFamily,
    fontWeight: 600,
    fontSize: 18, // same px as Atlas headingH4
    lineHeight: 28,
    letterSpacing: 0.2,
  },
  body: {
    fontFamily: vaultBodyFontFamily,
    fontWeight: 400,
    fontSize: 16, // same px as Atlas body
    lineHeight: 24, // same as Atlas (Vault textStyleHeading-based body)
    letterSpacing: 0,
  },
  bodySmall: {
    fontFamily: vaultBodyFontFamily,
    fontWeight: 400,
    fontSize: 14, // same px as Atlas bodySmall
    lineHeight: 24, // Vault textStyleBody was 24 (Atlas is 20, we use Vault to match density)
    letterSpacing: 0,
  },
  caption: {
    fontFamily: vaultBodyFontFamily,
    fontWeight: 400,
    fontSize: 12, // same px as Atlas caption
    lineHeight: 20, // Vault textStyleBodySmall was 20 (Atlas is 16)
    letterSpacing: 0,
  },
  emphasis: {
    fontFamily: vaultBodyFontFamily,
    fontWeight: 500, // same px as Atlas emphasis
    fontSize: 16, // same px as Atlas emphasis
    lineHeight: 24, // same px as Atlas emphasis
    letterSpacing: 0.2, // same px as Atlas emphasis
  },
  button: {
    fontFamily: vaultBodyFontFamily,
    fontWeight: 500, // same px as Atlas emphasis
    fontSize: 16, // same px as Atlas emphasis
    lineHeight: 25.5, // same px as Atlas button
    letterSpacing: 0.01, // same px as Atlas button
  },
} satisfies Record<keyof AtlasTypographySemantic, AtlasTypographyToken>;
